import React, { useState, useEffect, memo, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { supportAPI } from '../services/api';
import { useNotification } from '../contexts/NotificationContext';
import { handleApiError } from '../utils/errorHandler';

// Extract CreateTicketModal as a separate memoized component
const CreateTicketModal = memo(({ 
  showCreateModal, 
  setShowCreateModal, 
  newTicket, 
  setNewTicket, 
  categories, 
  createTicket, 
  loading 
}) => (
  <AnimatePresence>
    {showCreateModal && (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
        onClick={() => setShowCreateModal(false)}
      >
        <motion.div
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.9, opacity: 0 }}
          className="bg-white rounded-lg p-6 max-w-md w-full max-h-[90vh] overflow-y-auto"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-semibold">Create Support Ticket</h3>
            <button
              onClick={() => setShowCreateModal(false)}
              className="text-gray-400 hover:text-gray-600"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <form onSubmit={createTicket} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Subject *
              </label>
              <input
                type="text"
                value={newTicket.subject}
                onChange={(e) => setNewTicket({ ...newTicket, subject: e.target.value })}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="Brief description of your issue"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Category *
              </label>
              <select
                value={newTicket.category}
                onChange={(e) => setNewTicket({ ...newTicket, category: e.target.value })}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                required
              >
                {Object.entries(categories).map(([key, value]) => (
                  <option key={key} value={key}>{value}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Priority
              </label>
              <select
                value={newTicket.priority}
                onChange={(e) => setNewTicket({ ...newTicket, priority: e.target.value })}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="low">Low</option>
                <option value="medium">Medium</option>
                <option value="high">High</option>
                <option value="urgent">Urgent</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Description *
              </label>
              <textarea
                value={newTicket.description}
                onChange={(e) => setNewTicket({ ...newTicket, description: e.target.value })}
                rows={5}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="Please provide detailed information about your issue..."
                required
              />
            </div>

            <div className="flex gap-3">
              <button
                type="button"
                onClick={() => setShowCreateModal(false)}
                className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading}
                className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {loading ? 'Creating...' : 'Create Ticket'}
              </button>
            </div>
          </form>
        </motion.div>
      </motion.div>
    )}
  </AnimatePresence>
));

const Support = () => {
  const [activeTab, setActiveTab] = useState('tickets');
  const [tickets, setTickets] = useState([]);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [categories, setCategories] = useState({});
  const [loading, setLoading] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [replyText, setReplyText] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [filters, setFilters] = useState({
    status: '',
    category: '',
    priority: ''
  });

  const notification = useNotification();

  // Form state for new ticket
  const [newTicket, setNewTicket] = useState({
    subject: '',
    description: '',
    category: 'general',
    priority: 'medium'
  });

  useEffect(() => {
    loadInitialData();
  }, []);

  useEffect(() => {
    if (activeTab === 'tickets') {
      loadTickets();
    }
  }, [activeTab, filters]);

  const loadInitialData = async () => {
    try {
      const categoriesResponse = await supportAPI.getCategories();
      if (categoriesResponse?.status === 'success') {
        setCategories(categoriesResponse.data.categories);
      }
    } catch (err) {
      console.error('Failed to load initial data:', err);
    }
  };

  // Memoize the loadTickets function
  const loadTickets = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filters.status) params.status = filters.status;
      if (filters.category) params.category = filters.category;
      if (filters.priority) params.priority = filters.priority;

      const response = await supportAPI.getTickets(params);
      if (response?.status === 'success') {
        setTickets(response.data?.tickets || []);
      }
    } catch (err) {
      console.error('Failed to load tickets:', err);
      handleApiError(err, notification);
      setTickets([]); // Ensure tickets is always an array on error
    } finally {
      setLoading(false);
    }
  }, [filters, notification]);

  // Memoize the createTicket function to prevent recreating on every render
  const createTicket = useCallback(async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const response = await supportAPI.createTicket(newTicket);
      if (response?.status === 'success') {
        notification.showSuccess('Support ticket created successfully!', 'Success');
        setShowCreateModal(false);
        setNewTicket({
          subject: '',
          description: '',
          category: 'general',
          priority: 'medium'
        });
        loadTickets();
      }
    } catch (err) {
      console.error('Failed to create ticket:', err);
      handleApiError(err, notification);
    } finally {
      setLoading(false);
    }
  }, [newTicket, notification, loadTickets]);

  const loadTicketDetails = async (ticketId) => {
    setLoading(true);
    try {
      const response = await supportAPI.getTicket(ticketId);
      if (response?.status === 'success') {
        setSelectedTicket(response.data.ticket);
      }
    } catch (err) {
      console.error('Failed to load ticket details:', err);
      handleApiError(err, notification);
    } finally {
      setLoading(false);
    }
  };

  const addReply = async (e) => {
    e.preventDefault();
    if (!replyText.trim() || !selectedTicket) return;

    setLoading(true);
    try {
      const response = await supportAPI.addReply(selectedTicket.id, {
        message: replyText
      });
      
      if (response?.status === 'success') {
        notification.showSuccess('Reply added successfully', 'Success');
        setReplyText('');
        loadTicketDetails(selectedTicket.id);
      }
    } catch (err) {
      console.error('Failed to add reply:', err);
      handleApiError(err, notification);
    } finally {
      setLoading(false);
    }
  };

  const updateTicketStatus = async (ticketId, status) => {
    setLoading(true);
    try {
      const response = await supportAPI.updateStatus(ticketId, status);
      if (response?.status === 'success') {
        notification.showSuccess(response.message, 'Success');
        loadTickets();
        if (selectedTicket && selectedTicket.id === ticketId) {
          loadTicketDetails(ticketId);
        }
      }
    } catch (err) {
      console.error('Failed to update status:', err);
      handleApiError(err, notification);
    } finally {
      setLoading(false);
    }
  };

  const searchTickets = async () => {
    if (!searchQuery.trim()) {
      loadTickets();
      return;
    }

    setLoading(true);
    try {
      const response = await supportAPI.search(searchQuery);
      if (response?.status === 'success') {
        setTickets(response.data?.tickets || []);
      }
    } catch (err) {
      console.error('Failed to search tickets:', err);
      handleApiError(err, notification);
      setTickets([]); // Ensure tickets is always an array on error
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'open': return 'bg-blue-100 text-blue-800';
      case 'in_progress': return 'bg-yellow-100 text-yellow-800';
      case 'waiting_customer': return 'bg-orange-100 text-orange-800';
      case 'resolved': return 'bg-green-100 text-green-800';
      case 'closed': return 'bg-gray-100 text-gray-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'low': return 'bg-gray-100 text-gray-800';
      case 'medium': return 'bg-blue-100 text-blue-800';
      case 'high': return 'bg-orange-100 text-orange-800';
      case 'urgent': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const TicketList = () => (
    <div className="space-y-4">
      {/* Search and Filters */}
      <div className="bg-white rounded-lg border p-4 space-y-4">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1">
            <div className="relative">
              <input
                type="text"
                placeholder="Search tickets..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && searchTickets()}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
              <svg className="w-5 h-5 text-gray-400 absolute left-3 top-2.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </div>
          </div>
          <button
            onClick={searchTickets}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            Search
          </button>
          <button
            onClick={() => setShowCreateModal(true)}
            className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
          >
            New Ticket
          </button>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap gap-4">
          <select
            value={filters.status}
            onChange={(e) => setFilters({ ...filters, status: e.target.value })}
            className="border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="">All Status</option>
            <option value="open">Open</option>
            <option value="in_progress">In Progress</option>
            <option value="waiting_customer">Waiting for Customer</option>
            <option value="resolved">Resolved</option>
            <option value="closed">Closed</option>
          </select>

          <select
            value={filters.category}
            onChange={(e) => setFilters({ ...filters, category: e.target.value })}
            className="border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="">All Categories</option>
            {Object.entries(categories).map(([key, value]) => (
              <option key={key} value={key}>{value}</option>
            ))}
          </select>

          <select
            value={filters.priority}
            onChange={(e) => setFilters({ ...filters, priority: e.target.value })}
            className="border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="">All Priorities</option>
            <option value="low">Low</option>
            <option value="medium">Medium</option>
            <option value="high">High</option>
            <option value="urgent">Urgent</option>
          </select>
        </div>
      </div>

      {/* Tickets List */}
      <div className="space-y-3">
        {tickets.length === 0 ? (
          <div className="bg-white rounded-lg border p-8 text-center">
            <div className="w-16 h-16 mx-auto bg-gray-100 rounded-full flex items-center justify-center mb-4">
              <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
              </svg>
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">No support tickets found</h3>
            <p className="text-gray-600 mb-4">Create your first support ticket to get help from our team.</p>
            <button
              onClick={() => setShowCreateModal(true)}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              Create New Ticket
            </button>
          </div>
        ) : (
          tickets.map((ticket) => (
            <motion.div
              key={ticket.id}
              whileHover={{ scale: 1.01 }}
              className="bg-white rounded-lg border p-4 cursor-pointer hover:shadow-md transition-shadow"
              onClick={() => {
                setSelectedTicket(ticket);
                setActiveTab('detail');
                loadTicketDetails(ticket.id);
              }}
            >
              <div className="flex justify-between items-start mb-2">
                <div>
                  <h3 className="font-semibold text-gray-900">{ticket.subject}</h3>
                  <p className="text-sm text-gray-600">#{ticket.ticket_number}</p>
                </div>
                <div className="flex gap-2">
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${getPriorityColor(ticket.priority)}`}>
                    {ticket.priority_display}
                  </span>
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(ticket.status)}`}>
                    {ticket.status_display}
                  </span>
                </div>
              </div>
              
              <p className="text-sm text-gray-600 mb-3 line-clamp-2">{ticket.description}</p>
              
              <div className="flex justify-between items-center text-xs text-gray-500">
                <span>{categories[ticket.category] || ticket.category}</span>
                <span>{ticket.time_since_created}</span>
              </div>
            </motion.div>
          ))
        )}
      </div>
    </div>
  );

  const TicketDetail = () => {
    if (!selectedTicket) return null;

    return (
      <div className="space-y-6">
        {/* Back Button */}
        <button
          onClick={() => setActiveTab('tickets')}
          className="flex items-center text-blue-600 hover:text-blue-700"
        >
          <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          Back to Tickets
        </button>

        {/* Ticket Header */}
        <div className="bg-white rounded-lg border p-6">
          <div className="flex justify-between items-start mb-4">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">{selectedTicket.subject}</h1>
              <p className="text-gray-600">#{selectedTicket.ticket_number}</p>
            </div>
            <div className="flex gap-2">
              <span className={`px-3 py-1 rounded-full text-sm font-medium ${getPriorityColor(selectedTicket.priority)}`}>
                {selectedTicket.priority_display}
              </span>
              <span className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(selectedTicket.status)}`}>
                {selectedTicket.status_display}
              </span>
            </div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
            <div>
              <p className="text-sm text-gray-600">Category</p>
              <p className="font-medium">{categories[selectedTicket.category] || selectedTicket.category}</p>
            </div>
            <div>
              <p className="text-sm text-gray-600">Created</p>
              <p className="font-medium">{selectedTicket.time_since_created}</p>
            </div>
            <div>
              <p className="text-sm text-gray-600">Priority</p>
              <p className="font-medium">{selectedTicket.priority_display}</p>
            </div>
            <div>
              <p className="text-sm text-gray-600">Status</p>
              <p className="font-medium">{selectedTicket.status_display}</p>
            </div>
          </div>

          <div className="border-t pt-4">
            <h3 className="font-semibold mb-2">Description</h3>
            <p className="text-gray-700 whitespace-pre-wrap">{selectedTicket.description}</p>
          </div>

          {/* Status Actions */}
          {selectedTicket.status !== 'closed' && (
            <div className="border-t pt-4 mt-4">
              <div className="flex gap-2">
                {selectedTicket.status !== 'resolved' && (
                  <button
                    onClick={() => updateTicketStatus(selectedTicket.id, 'resolved')}
                    className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-sm"
                  >
                    Mark as Resolved
                  </button>
                )}
                <button
                  onClick={() => updateTicketStatus(selectedTicket.id, 'closed')}
                  className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors text-sm"
                >
                  Close Ticket
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Replies */}
        <div className="bg-white rounded-lg border p-6">
          <h3 className="text-lg font-semibold mb-4">Conversation</h3>
          
          <div className="space-y-4 mb-6">
            {selectedTicket.replies && selectedTicket.replies.length > 0 ? (
              selectedTicket.replies.map((reply) => (
                <div
                  key={reply.id}
                  className={`p-4 rounded-lg ${
                    reply.is_staff_reply
                      ? 'bg-blue-50 border-l-4 border-blue-500'
                      : 'bg-gray-50 border-l-4 border-gray-300'
                  }`}
                >
                  <div className="flex justify-between items-start mb-2">
                    <div className="flex items-center gap-2">
                      <span className="font-medium">
                        {reply.is_staff_reply ? 'Support Team' : reply.user?.name || 'You'}
                      </span>
                      {reply.is_staff_reply && (
                        <span className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full">
                          Staff
                        </span>
                      )}
                    </div>
                    <span className="text-sm text-gray-500">
                      {reply.time_since_created}
                    </span>
                  </div>
                  <p className="text-gray-700 whitespace-pre-wrap">{reply.message}</p>
                </div>
              ))
            ) : (
              <p className="text-gray-600 text-center py-4">No replies yet. Our support team will respond soon.</p>
            )}
          </div>

          {/* Reply Form */}
          {selectedTicket.status !== 'closed' && (
            <form onSubmit={addReply} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Add Reply
                </label>
                <textarea
                  value={replyText}
                  onChange={(e) => setReplyText(e.target.value)}
                  rows={4}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Type your reply here..."
                  required
                />
              </div>
              <button
                type="submit"
                disabled={!replyText.trim() || loading}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {loading ? 'Sending...' : 'Send Reply'}
              </button>
            </form>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="max-w-7xl mx-auto p-6 space-y-6">
      {/* Header */}
      <div className="text-center">
        <h1 className="text-3xl font-bold text-gray-900">Customer Support</h1>
        <p className="text-gray-600 mt-2">Get help with your banking needs</p>
      </div>

      {/* Tabs */}
      <div className="flex justify-center">
        <div className="bg-gray-100 p-1 rounded-lg">
          <button
            onClick={() => setActiveTab('tickets')}
            className={`px-6 py-2 rounded-md font-medium transition-colors ${
              activeTab === 'tickets'
                ? 'bg-white text-blue-600 shadow-sm'
                : 'text-gray-600 hover:text-gray-800'
            }`}
          >
            My Tickets
          </button>
          <button
            onClick={() => setActiveTab('faq')}
            className={`px-6 py-2 rounded-md font-medium transition-colors ${
              activeTab === 'faq'
                ? 'bg-white text-blue-600 shadow-sm'
                : 'text-gray-600 hover:text-gray-800'
            }`}
          >
            FAQ
          </button>
        </div>
      </div>

      {/* Content */}
      {loading && activeTab === 'tickets' ? (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      ) : (
        <>
          {activeTab === 'tickets' && <TicketList />}
          {activeTab === 'detail' && <TicketDetail />}
          {activeTab === 'faq' && (
            <div className="bg-white rounded-lg border p-6">
              <h2 className="text-xl font-semibold mb-4">Frequently Asked Questions</h2>
              <div className="space-y-4">
                <div>
                  <h3 className="font-medium text-gray-900 mb-2">How do I reset my password?</h3>
                  <p className="text-gray-600">You can reset your password by clicking the "Forgot Password" link on the login page.</p>
                </div>
                <div>
                  <h3 className="font-medium text-gray-900 mb-2">How do I transfer money?</h3>
                  <p className="text-gray-600">Use the Transfer section in your dashboard to send money between accounts or to other banks.</p>
                </div>
                <div>
                  <h3 className="font-medium text-gray-900 mb-2">What are the transfer limits?</h3>
                  <p className="text-gray-600">Daily transfer limits vary by account type. Check the Transfer section for your specific limits.</p>
                </div>
                <div>
                  <h3 className="font-medium text-gray-900 mb-2">How do I contact customer service?</h3>
                  <p className="text-gray-600">Create a support ticket above or email us at support@firstcapitalsint.com for immediate assistance.</p>
                </div>
              </div>
            </div>
          )}
        </>
      )}

      {/* Create Ticket Modal */}
      <CreateTicketModal 
        showCreateModal={showCreateModal} 
        setShowCreateModal={setShowCreateModal} 
        newTicket={newTicket} 
        setNewTicket={setNewTicket} 
        categories={categories} 
        createTicket={createTicket} 
        loading={loading} 
      />
    </div>
  );
};

export default Support; 